import { apiUrlV2 } from "../constants/apiUrl"
import { placeTypeNameToLocationType } from "../constants/carpoolLocation"
import rollbarLogger from "../utilities/rollbarLogger"

const logCarpoolErrorWithoutNotification = (get, message, error) => {
  rollbarLogger(get().user).logError(message, error)
}

const extractCarpoolErrorMessage = (data, fallback) => {
  const error = data?.error
  if (Array.isArray(error)) {
    // DFP VALIDATION ERRORS
    const joined = error.filter(Boolean).join(' ')
    if (joined) return joined
  } else if (typeof error === 'string' && error) {
    // UNEXPECTED RESPONSE RESCUED BY DFP
    return error
  } else if (error?.details) {
    // CARPOOL VALIDATION ERRORS COME IN AS WITH THE MESSAGES IN THE DETAILS HASH
    const detailMessages = Object.values(error.details).flat().filter(Boolean)
    if (detailMessages.length) return detailMessages.join(' ')
  }
  // RETURN FALLBACK IF INCOMING ERROR MESSAGE IS ODDLY FORMATTED
  return fallback
}

const NHTSA_VIN_URL = 'https://vpic.nhtsa.dot.gov/api/vehicles/decodevin'

const initialState = {
  isCreatingCarpoolQuote: false,
  isSigningTransportationAgreement: false,
  carpoolQuote: null,
  unitQuotes: [],
  unitQuotesFetching: false,
  dealerQuotes: [],
  dealerQuotesFetching: false,
  dealerOrders: [],
  dealerOrdersFetching: false,
  carpoolOrder: null,
  auctions: [],
  isFetchingAuctions: false,
  carpoolDealerPlaces: [],
  isFetchingCarpoolDealerPlaces: false,
}

const createCarpoolSlice = (set, get) => ({
  ...initialState,
  createCarpoolQuote: async (carpoolQuote) => {
    set(() => ({ isCreatingCarpoolQuote: true }))
    try {
      const response = await fetch(
        `${apiUrlV2}/carpool/carpool_quotes`,
        {
          headers: get().requestHeaders,
          method: "POST",
          body: JSON.stringify(carpoolQuote)
        }
      )
      const data = await response.json()

      if (response.ok) {
        set({ carpoolQuote: data.data })
        return data.data
      } else {
        logCarpoolErrorWithoutNotification(get, 'Error creating carpool quote.', data)
        throw new Error(extractCarpoolErrorMessage(data, 'Error creating carpool quote.'))
      }
    } catch (err) {
      logCarpoolErrorWithoutNotification(get, 'Error creating carpool quote.', err)
      throw err
    } finally {
      set(() => ({ isCreatingCarpoolQuote: false }))
    }
  },
  getCarpoolQuotesForUnit: async (unitId) => {
    set(() => ({ unitQuotesFetching: true }))
    try {
      const response = await fetch(
        `${apiUrlV2}/carpool/carpool_quotes?` +
        new URLSearchParams({
          dealer_id: get().dealer.id, unit_id: unitId
        }),
        {
          headers: get().requestHeaders,
          method: "GET"
        }
      )
      const data = await response.json()

      if (response.ok) {
        set(() => ({ unitQuotes: data.data || [] }))
      } else {
        get().logError('Error fetching carpool quotes for unit.', data)
        set(() => ({ unitQuotes: [] }))
      }
    } catch (err) {
      console.log(err, 'error fetching carpool quotes for unit')
      get().logError('Error fetching carpool quotes for unit.', err)
      set(() => ({ unitQuotes: [] }))
    } finally {
      set(() => ({ unitQuotesFetching: false }))
    }
  },
  getDealerCarpoolQuotes: async () => {
    set(() => ({ dealerQuotesFetching: true }))
    try {
      const response = await fetch(
        `${apiUrlV2}/carpool/carpool_quotes?` +
        new URLSearchParams({ dealer_id: get().dealer.id }),
        {
          headers: get().requestHeaders,
          method: 'GET'
        }
      )
      const data = await response.json()

      if (response.ok) {
        set(() => ({ dealerQuotes: data.data || [] }))
      } else {
        get().logError('Error fetching dealer carpool quotes.', data)
        set(() => ({ dealerQuotes: [] }))
      }
    } catch (err) {
      get().logError('Error fetching dealer carpool quotes.', err)
      set(() => ({ dealerQuotes: [] }))
    } finally {
      set(() => ({ dealerQuotesFetching: false }))
    }
  },
  getCarpoolQuote: async (carpoolObjectId) => {
    try {
      const response = await fetch(
        `${apiUrlV2}/carpool/carpool_quotes/${carpoolObjectId}?` +
        new URLSearchParams({ dealer_id: get().dealer.id }),
        {
          headers: get().requestHeaders,
          method: "GET"
        }
      )
      const data = await response.json()

      if (response.ok) {
        set({ carpoolQuote: data.data })
      } else {
        get().logError('Error fetching carpool quote.', data)
        throw new Error(data.message || 'Error fetching carpool quote.')
      }
    } catch (err) {
      console.log(err, 'error fetching carpool quote')
      get().logError('Error fetching carpool quote.', err)
      throw err
    }
  },
  getDealerCarpoolOrders: async () => {
    set(() => ({ dealerOrdersFetching: true }))
    try {
      const response = await fetch(
        `${apiUrlV2}/carpool/carpool_orders?` +
        new URLSearchParams({ dealer_id: get().dealer.id }),
        {
          headers: get().requestHeaders,
          method: 'GET'
        }
      )
      const data = await response.json()

      if (response.ok) {
        set(() => ({ dealerOrders: data.data || [] }))
      } else {
        get().logError('Error fetching dealer carpool orders.', data)
        set(() => ({ dealerOrders: [] }))
      }
    } catch (err) {
      get().logError('Error fetching dealer carpool orders.', err)
      set(() => ({ dealerOrders: [] }))
    } finally {
      set(() => ({ dealerOrdersFetching: false }))
    }
  },
  getCarpoolOrder: async (carpoolObjectId) => {
    try {
      const response = await fetch(
        `${apiUrlV2}/carpool/carpool_orders/${carpoolObjectId}?` +
        new URLSearchParams({ dealer_id: get().dealer.id }),
        {
          headers: get().requestHeaders,
          method: 'GET'
        }
      )
      const data = await response.json()

      if (response.ok) {
        set({ carpoolOrder: data.data })
      } else {
        get().logError('Error fetching carpool order.', data)
        throw new Error(data.message || 'Error fetching carpool order.')
      }
    } catch (err) {
      get().logError('Error fetching carpool order.', err)
      throw err
    }
  },
  createCarpoolOrder: async (orderPayload) => {
    try {
      const response = await fetch(`${apiUrlV2}/carpool/carpool_orders`, {
        headers: get().requestHeaders,
        method: 'POST',
        body: JSON.stringify(orderPayload),
      })
      const data = await response.json()

      if (response.ok) {
        get().getDealerCarpoolQuotes()
        return data.data
      } else {
        logCarpoolErrorWithoutNotification(get, 'Error creating carpool order.', data)
        throw new Error(extractCarpoolErrorMessage(data, 'Error creating carpool order.'))
      }
    } catch (err) {
      logCarpoolErrorWithoutNotification(get, 'Error creating carpool order.', err)
      throw err
    }
  },
  cancelCarpoolOrder: async (carpoolObjectId) => {
    try {
      const response = await fetch(
        `${apiUrlV2}/carpool/carpool_orders/${carpoolObjectId}/cancel?` +
        new URLSearchParams({ dealer_id: get().dealer.id }),
        {
          headers: get().requestHeaders,
          method: 'PUT',
        },
      )
      const data = await response.json()

      if (response.ok) {
        set({ carpoolOrder: data.data })
        get().getDealerCarpoolOrders()
        return data.data
      } else {
        get().logError('Error cancelling carpool order.', data)
        throw new Error(data.message || 'Error cancelling carpool order.')
      }
    } catch (err) {
      get().logError('Error cancelling carpool order.', err)
      throw err
    }
  },
  signTransportationAgreement: async () => {
    set({ isSigningTransportationAgreement: true })
    try {
      const response = await fetch(`${apiUrlV2}/dealers/sign_transportation_agreement`, {
        method: 'PATCH',
        headers: get().requestHeaders,
      })
      const data = await response.json()

      if (response.ok) {
        set({ dealer: data.data })
      } else {
        get().logError('Error signing transportation agreement.', data)
      }
    } catch (err) {
      get().logError('Error signing transportation agreement.', err)
    } finally {
      set({ isSigningTransportationAgreement: false })
    }
  },
  decodeVin: async (vin) => {
    try {
      const response = await fetch(`${NHTSA_VIN_URL}/${vin}?format=json`)
      const data = await response.json()
      const get = (label) => data.Results.find((r) => r.Variable === label)?.Value || null
      return {
        year: get('Model Year'),
        make: get('Make'),
        model: get('Model'),
        trim: get('Trim'),
      }
    } catch (err) {
      get().logError('Error decoding VIN via NHTSA.', err)
      return null
    }
  },
  fetchAuctions: async () => {
    set(() => ({ isFetchingAuctions: true }))
    try {
      const url = `${apiUrlV2}/auctions?` + new URLSearchParams({ dealer_id: get().dealer.id })
      const response = await fetch(url, { headers: get().requestHeaders, method: 'GET' })
      const data = await response.json()

      if (response.ok) {
        const normalized = data.data.map((auction) => ({
          id: auction.id,
          name: auction.attributes.name,
          address: auction.attributes.address1,
          city: auction.attributes.city,
          state: auction.attributes.state,
          postal_code: auction.attributes.zip,
        }))
        set(() => ({ auctions: normalized }))
      } else {
        get().logError('Error fetching auctions.', data)
        set(() => ({ auctions: [] }))
      }
    } catch (err) {
      console.error('[fetchAuctions] error', err)
      get().logError('Error fetching auctions.', err)
      set(() => ({ auctions: [] }))
    } finally {
      set(() => ({ isFetchingAuctions: false }))
    }
  },
  searchAuctions: async (term) => {
    if (!term?.trim()) return []
    try {
      const url = `${apiUrlV2}/auctions/search?` + new URLSearchParams({ term: term.trim() })
      const response = await fetch(url, { headers: get().requestHeaders, method: 'GET' })
      const data = await response.json()

      if (response.ok) {
        console.log(data)
        return data.data.map((auction) => ({
          id: auction.id,
          name: auction.attributes.name,
          address: auction.attributes.address1,
          city: auction.attributes.city,
          state: auction.attributes.state,
          postal_code: auction.attributes.zip,
        }))
      }
      get().logError('Error searching auctions.', data)
      return []
    } catch (err) {
      console.error('[searchAuctions] error', err)
      get().logError('Error searching auctions.', err)
      return []
    }
  },
  createCarpoolDealerPlace: async (dealerPlace) => {
    const dealerId = get().dealer?.id
    if (!dealerId) return null
    try {
      const response = await fetch(
        `${apiUrlV2}/carpool/dealer_places`,
        {
          headers: get().requestHeaders,
          method: 'POST',
          body: JSON.stringify({ dealer_id: dealerId, dealer_place: dealerPlace }),
        },
      )
      const data = await response.json()
      if (response.ok) {
        get().fetchCarpoolDealerPlaces()
        return data.data
      } else {
        get().logError('Error creating carpool dealer place.', data)
        return null
      }
    } catch (err) {
      get().logError('Error creating carpool dealer place.', err)
      return null
    }
  },
  fetchCarpoolDealerPlaces: async () => {
    const dealerId = get().dealer?.id
    if (!dealerId) return
    set(() => ({ isFetchingCarpoolDealerPlaces: true }))
    try {
      const response = await fetch(
        `${apiUrlV2}/carpool/dealer_places?` + new URLSearchParams({ dealer_id: dealerId }),
        { headers: get().requestHeaders, method: 'GET' },
      )
      const data = await response.json()
      if (response.ok) {
        const normalized = (data.data || []).map((place) => {
          const attrs = place.attributes || place
          const placeTypeName = attrs['place-type-name'] || attrs.place_type_name || ''
          return {
            id: place.id,
            name:
              attrs.name ||
              attrs['dealer-name'] ||
              attrs.dealer_name ||
              placeTypeName ||
              '',
            address: attrs['address-1'] || attrs.address1 || '',
            address_2: attrs['address-2'] || attrs.address2 || '',
            city: attrs.city || '',
            state: attrs.state || '',
            postal_code: attrs.zip || attrs['postal-code'] || '',
            place_type: placeTypeNameToLocationType[placeTypeName] || null,
          }
        })
        set(() => ({ carpoolDealerPlaces: normalized }))
      } else {
        get().logError('Error fetching carpool dealer places.', data)
        set(() => ({ carpoolDealerPlaces: [] }))
      }
    } catch (err) {
      get().logError('Error fetching carpool dealer places.', err)
      set(() => ({ carpoolDealerPlaces: [] }))
    } finally {
      set(() => ({ isFetchingCarpoolDealerPlaces: false }))
    }
  },
  setCarpoolQuote: (details) => set({ carpoolQuote: details }),
  resetCarpoolSlice: () => set(initialState)
})

export default createCarpoolSlice